<template>
  <div class="content">
    <PostBacklink />
    <section>
      <div class="row">
        <div class="column column-50">
          <div class="post-content">
            <h2 class="mt-0">
              After Effects shortcuts quick reference because I never remember
              these.
            </h2>
            <p>
              This list isn't exhaustive, and it isn't just for me. I've
              included some more common shortcuts for your convenience,
              Stranger.
            </p>
            <div class="toggle-group" @click="togglePlatform">
              <div
                class="toggle-slider"
                :class="{ 'slide-right': platform === 'windows' }"
              ></div>
              <div
                class="toggle-group-item"
                :class="{ active: platform === 'mac' }"
              >
                <span>Mac</span>
              </div>
              <div
                class="toggle-group-item"
                :class="{ active: platform === 'windows' }"
              >
                <span>PC</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="column column-50">
          <h3>Viewer</h3>
          <div class="shortcuts-grid">
            <ShortcutGridItem
              description="Fit to Screen"
              :shortcut-items="
                convertShortcuts([
                  { type: 'key', value: 'Shift' },
                  { type: 'key', value: '/' },
                ])
              "
            />
          </div>
          <h3>Tools</h3>
          <div class="shortcuts-grid">
            <ShortcutGridItem
              description="Pan Behind"
              :shortcut-items="convertShortcuts([{ type: 'key', value: 'Y' }])"
            />
            <ShortcutGridItem
              description="Pen Tool"
              :shortcut-items="convertShortcuts([{ type: 'key', value: 'G' }])"
            />
            <ShortcutGridItem
              description="Rotation Tool"
              :shortcut-items="convertShortcuts([{ type: 'key', value: 'W' }])"
            />
            <ShortcutGridItem
              description="Shape Tool"
              :shortcut-items="convertShortcuts([{ type: 'key', value: 'Q' }])"
            />
          </div>
          <h3>Timeline</h3>
          <div class="shortcuts-grid">
            <ShortcutGridItem
              description="Jump to First Frame of Layer"
              :shortcut-items="convertShortcuts([{ type: 'key', value: 'I' }])"
            />
            <ShortcutGridItem
              description="Prev/Next Keyframe"
              :shortcut-items="
                convertShortcuts([
                  { type: 'key', value: 'J' },
                  { type: 'text', value: '/' },
                  { type: 'key', value: 'K' },
                ])
              "
            />
            <ShortcutGridItem
              description="Split Layer"
              :shortcut-items="
                convertShortcuts([
                  { type: 'key', value: 'Cmd' },
                  { type: 'key', value: 'Shift' },
                  { type: 'key', value: 'D' },
                ])
              "
            />
            <ShortcutGridItem
              description="Trim to Playhead"
              :shortcut-items="
                convertShortcuts([
                  { type: 'key', value: 'Option' },
                  { type: 'key', value: '[' },
                  { type: 'text', value: '/' },
                  { type: 'key', value: ']' },
                ])
              "
            />
          </div>
          <h3>Composition</h3>
          <div class="shortcuts-grid">
            <ShortcutGridItem
              description="Composition Settings"
              :shortcut-items="
                convertShortcuts([
                  { type: 'key', value: 'Cmd' },
                  { type: 'key', value: 'K' },
                ])
              "
            />
            <ShortcutGridItem
              description="New Composition"
              :shortcut-items="
                convertShortcuts([
                  { type: 'key', value: 'Cmd' },
                  { type: 'key', value: 'N' },
                ])
              "
            />
          </div>

          <h3>Layers</h3>
          <div class="shortcuts-grid">
            <ShortcutGridItem
              description="New Solid"
              :shortcut-items="
                convertShortcuts([
                  { type: 'key', value: 'Cmd' },
                  { type: 'key', value: 'Y' },
                ])
              "
            />
          </div>
          <h3>Layer properties</h3>
          <div class="shortcuts-grid">
            <ShortcutGridItem
              description="Opacity"
              :shortcut-items="
                convertShortcuts([
                  { type: 'text', value: '(for some reason)' },
                  { type: 'key', value: 'T' },
                ])
              "
            />
            <ShortcutGridItem
              description="Position"
              :shortcut-items="convertShortcuts([{ type: 'key', value: 'P' }])"
            />
            <ShortcutGridItem
              description="Rotation"
              :shortcut-items="convertShortcuts([{ type: 'key', value: 'R' }])"
            />
            <ShortcutGridItem
              description="Scale"
              :shortcut-items="convertShortcuts([{ type: 'key', value: 'S' }])"
            />
            <ShortcutGridItem
              description="Show Modified Keyframes"
              :shortcut-items="convertShortcuts([{ type: 'key', value: 'U' }])"
            />
            <ShortcutGridItem
              description="Toggle Applied Effects"
              :shortcut-items="convertShortcuts([{ type: 'key', value: 'E' }])"
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
<script setup>
import { ref, onMounted } from "vue";
import PostBacklink from "@/components/posts/PostBacklink.vue";
import ShortcutGridItem from "@/components/shortcuts/ShortcutGridItem.vue";

// Cookie utility functions
const setCookie = (name, value, days = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const platform = ref("mac");

const keyMappings = {
  mac: {
    Cmd: "Cmd",
    Option: "Option",
    Shift: "Shift",
    Ctrl: "Ctrl",
  },
  windows: {
    Cmd: "Ctrl",
    Option: "Alt",
    Shift: "Shift",
    Ctrl: "Ctrl",
  },
};

const convertShortcuts = (shortcuts) => {
  return shortcuts.map((shortcut) => {
    if (
      shortcut.type === "key" &&
      keyMappings[platform.value][shortcut.value]
    ) {
      return {
        ...shortcut,
        value: keyMappings[platform.value][shortcut.value],
      };
    }
    return shortcut;
  });
};

const togglePlatform = () => {
  platform.value = platform.value === "mac" ? "windows" : "mac";
  setCookie("ae-shortcuts-platform", platform.value);
};

// Load saved preference on mount
onMounted(() => {
  const savedPlatform = getCookie("ae-shortcuts-platform");
  if (
    savedPlatform &&
    (savedPlatform === "mac" || savedPlatform === "windows")
  ) {
    platform.value = savedPlatform;
  }
});
</script>

<script>
// Export metadata for automatic discovery
export const postMeta = {
  title: "A cheat sheet for After Effects.",
  description:
    "A cheat sheet of shortcuts for the most unintuitive piece of software Adobe has ever made. And that's saying a lot.",
  tags: ["June 24, 2025"],
  date: "2025-01-15",
  image: {
    type: "video",
    src: "@/assets/posts/alt-f4.mp4",
    mimeType: "video/mp4",
    alt: "After Effects shortcuts demonstration",
  },
};
</script>

<style scoped>
.shortcuts-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  align-items: center;
}

.toggle-group {
  display: inline-flex;
  border: 1px solid var(--color-border-light);
  overflow: hidden;
  padding: 4px;
  font-family: "Departure Mono", monospace;
  text-transform: uppercase;
  font-size: 11px;
  position: relative;
}

.toggle-slider {
  position: absolute;
  top: 4px;
  left: 4px;
  width: calc(50% - 4px);
  height: calc(100% - 8px);
  background-color: var(--color-primary, #007bff);
  transition: transform 0.2s ease;
  z-index: 1;
}

.toggle-slider.slide-right {
  transform: translateX(100%);
}

.toggle-group-item {
  padding: 2px;
  text-align: center;
  cursor: pointer;
  background-color: transparent;
  transition: all 0.2s ease;
  min-width: 4rem;
  position: relative;
  z-index: 2;
}

.toggle-group-item:hover {
  background-color: transparent;
}

.toggle-group-item.active {
  color: #111;
}

.toggle-group-item span {
  font-weight: 500;
}
</style>
